/**
 * Photos d'une éval (migration 0333) : copie corrigée + énoncé. L'élève qui a
 * un prof les ajoute (appareil photo ou galerie, PDF accepté) ; son prof les
 * voit en lecture seule sur la fiche élève. Toucher une photo l'ouvre en grand.
 */

import { useRef, useState } from 'react';
import { Camera, FileText, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  EVALUATION_PHOTO_KIND_LABEL,
  EVALUATION_PHOTO_TYPES,
  evaluationPhotoErrorMessage,
  usePhotoUrls,
  useDeleteEvaluationPhoto,
  useUploadEvaluationPhotos,
  type EvaluationPhoto,
  type EvaluationPhotoKind,
} from '@/lib/evaluations/photos';

export function EvaluationPhotos({
  studentId,
  evaluationId,
  photos,
  readOnly = false,
}: {
  studentId: string;
  evaluationId: string;
  /** Photos de CETTE éval. */
  photos: EvaluationPhoto[];
  readOnly?: boolean;
}) {
  const upload = useUploadEvaluationPhotos(studentId);
  const remove = useDeleteEvaluationPhoto(studentId);
  const urls = usePhotoUrls(photos.map((p) => p.storage_path));
  const [error, setError] = useState<string | null>(null);
  const inputs = {
    copie: useRef<HTMLInputElement>(null),
    enonce: useRef<HTMLInputElement>(null),
  };

  async function onFiles(kind: EvaluationPhotoKind, list: FileList | null) {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    setError(null);
    try {
      await upload.mutateAsync({ evaluationId, kind, files });
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg.startsWith('Format') || msg.startsWith('Fichier')
          ? msg
          : evaluationPhotoErrorMessage(msg),
      );
    }
  }

  if (readOnly && photos.length === 0) return null;

  return (
    <div className="space-y-2">
      {photos.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Photos de l’éval">
          {photos.map((p) => {
            const url = urls.data?.[p.storage_path];
            const isPdf = p.mime_type === 'application/pdf';
            return (
              <li key={p.id} className="relative">
                <a
                  href={url ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="border-ink-200 bg-ink-50 flex h-20 w-20 flex-col items-center justify-center overflow-hidden rounded-xl border"
                  aria-label={`${EVALUATION_PHOTO_KIND_LABEL[p.kind]} — ouvrir`}
                >
                  {isPdf || !url ? (
                    <FileText className="text-ink-400 h-8 w-8" aria-hidden />
                  ) : (
                    <img src={url} alt="" className="h-full w-full object-cover" />
                  )}
                </a>
                <span className="bg-ink-900/70 absolute bottom-1 left-1 rounded px-1 text-[11px] font-semibold text-white">
                  {EVALUATION_PHOTO_KIND_LABEL[p.kind]}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    aria-label="Supprimer la photo"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm('Supprimer cette photo ?')) remove.mutate(p);
                    }}
                    className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center"
                  >
                    <span className="bg-ink-900 flex h-6 w-6 items-center justify-center rounded-full text-white">
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </span>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!readOnly && (
        <div className="flex flex-col gap-2 sm:flex-row">
          {(['copie', 'enonce'] as const).map((kind) => (
            <div key={kind} className="flex-1">
              <input
                ref={inputs[kind]}
                type="file"
                accept={EVALUATION_PHOTO_TYPES.join(',')}
                multiple
                className="hidden"
                aria-label={kind === 'copie' ? 'Photos de ma copie' : 'Photos de l’énoncé'}
                onChange={(e) => {
                  void onFiles(kind, e.target.files);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                disabled={upload.isPending}
                onClick={() => inputs[kind].current?.click()}
                className={cn(
                  'border-ink-200 text-ink-800 ocean:bg-white/5 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border bg-white px-3 text-sm font-semibold active:scale-[0.98]',
                  upload.isPending && 'opacity-60',
                )}
              >
                <Camera className="h-4 w-4" aria-hidden />
                {kind === 'copie' ? 'Photo de ma copie' : 'Photo de l’énoncé'}
              </button>
            </div>
          ))}
        </div>
      )}
      {upload.isPending && <p className="text-ink-500 text-xs">Envoi des photos…</p>}
      {error && (
        <p role="alert" className="text-error-600 text-sm">
          {error}
        </p>
      )}
    </div>
  );
}
